import { useEffect, useMemo, useRef, useState } from 'react';
import { NotFoundInPage } from '@/components/NotFoundInPage';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './insurance-ar.css';
import type { ArClaim, ArTab } from './types';
import {
  addWriteOffReason,
  getBucketsSync,
  getClaimSync,
  getLateSync,
  getNotFollowedSync,
  getOntimeSync,
  getPaysPatientSync,
  getOwedSync,
  getClaimCountSync,
  getWrittenOffSync,
  lateCountSync,
  resumeFollowUp,
  stopFollowUp,
  useArVersion,
  waitingCountSync,
  writtenOffCountSync,
  writtenOffTotalSync,
} from './store';
import { usePaidToPatientVersion } from '@/features/networks/paidToPatient';
import { Header } from './components/Header';
import { List, MULTI_PAYER } from './components/List';
import { parsePayerParam, payerSlug as AR_SLUG_BY_NAME } from './payers';
import { getMovedToPatientText } from './store';
import { Detail } from './components/Detail';
import { StopDialog } from './components/StopDialog';
import { WrittenOff } from './components/WrittenOff';

interface Toast {
  id: number;
  msg: string;
  undoLabel?: string;
  onUndo?: () => void;
}

function useIsDesktop(): boolean {
  const [d, setD] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true));
  useEffect(() => {
    const m = window.matchMedia('(min-width: 1024px)');
    const h = () => { setD(m.matches); };
    m.addEventListener('change', h);
    return () => { m.removeEventListener('change', h); };
  }, []);
  return d;
}

/** Patient, claim no., member ID or amount (and payer / procedure for convenience). */
function matches(c: ArClaim, q: string): boolean {
  const t = q.trim().toLowerCase();
  if (t === '') return true;
  const hay = `${c.patient} ${c.payer.name} ${c.payer.short} ${c.procedure} ${c.id} ${c.memberId}`.toLowerCase();
  if (hay.includes(t)) return true;
  const digits = t.replace(/[^0-9.]/g, '');
  if (digits.length >= 2) {
    const amt = c.billed.toFixed(2);
    return amt.includes(digits) || amt.replace(/\.00$/, '').includes(digits);
  }
  return false;
}

/** URL value ↔ pill label. */
const AGE_PARAM: Record<string, string> = {
  '0-30': '0–30 days',
  '31-60': '31–60 days',
  '61-90': '61–90 days',
  '91-120': '91–120 days',
  '120+': 'Over 120 days',
  '120': 'Over 120 days',
};
const AGE_TO_PARAM: Record<string, string> = {
  '0–30 days': '0-30',
  '31–60 days': '31-60',
  '61–90 days': '61-90',
  '91–120 days': '91-120',
  'Over 120 days': '120+',
};

function inBucket(c: ArClaim, label: string | null): boolean {
  if (!label) return true;
  // The aging cards count from the date of service (D203).
  const d = c.serviceDays ?? c.days;
  switch (label) {
    case '0–30 days': return d <= 30;
    case '31–60 days': return d >= 31 && d <= 60;
    case '61–90 days': return d >= 61 && d <= 90;
    case '91–120 days': return d >= 91 && d <= 120;
    case 'Over 120 days': return d > 120;
    default: return true;
  }
}

export function InsuranceAr({ view = 'waiting' }: { view?: ArTab }) {
  useArVersion();
  usePaidToPatientVersion();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { claimId } = useParams();
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  // Filters live in the URL so links in (/ar?group=late, /ar?payer=aetna,delta)
  // and reloads land on the same view (§4.3 Links in).
  const search = params.get('q') ?? '';
  const payerSet = useMemo(() => parsePayerParam(params.get('payer')), [params]);
  const payerKey = [...payerSet].join('|');
  const bucket = AGE_PARAM[params.get('age') ?? ''] ?? null;
  const patchParams = (next: Record<string, string | null>) => {
    setParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(next)) {
          if (v === null || v === '') sp.delete(k);
          else sp.set(k, v);
        }
        return sp;
      },
      { replace: true },
    );
  };
  const keepSearch = location.search;
  const [stopOpen, setStopOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastSeq = useRef(0);

  const late = getLateSync();
  const ontime = getOntimeSync();
  const notFollowed = getNotFollowedSync();
  const paysAll = getPaysPatientSync();
  const selected = claimId ? getClaimSync(claimId) : null;

  const payerOk = (c: ArClaim) => payerSet.size === 0 || payerSet.has(c.payer.name);
  const lateRows = useMemo(
    () => late.filter((c) => matches(c, search) && payerOk(c) && inBucket(c, bucket)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [late, search, payerKey, bucket],
  );
  // The page search drives both queues (patient, claim no., member ID, amount).
  const ontimeRows = useMemo(
    () => ontime.filter((c) => matches(c, search) && payerOk(c) && inBucket(c, bucket)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ontime, search, payerKey, bucket],
  );
  // ELVA isn't following up: the same search, payer and age filters as the other groups.
  const notFollowedRows = useMemo(
    () => notFollowed.filter((c) => matches(c, search) && payerOk(c) && inBucket(c, bucket)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notFollowed, search, payerKey, bucket],
  );
  // "The payer pays the patient": search and payer apply; the age pills don't (these claims are in no bucket).
  const paysRows = useMemo(
    () => (bucket ? [] : paysAll.filter((c) => matches(c, search) && payerOk(c))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paysAll, search, payerKey, bucket],
  );
  // Payer choices carry their REAL counts under the other active filters, so
  // "Delta (19)" is exactly what the list then shows.
  const payerOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of [...late, ...ontime, ...notFollowed]) {
      if (matches(c, search) && inBucket(c, bucket)) counts.set(c.payer.name, (counts.get(c.payer.name) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [late, ontime, notFollowed, search, bucket]);
  const payerValue = payerSet.size === 0 ? '' : payerSet.size === 1 ? ([...payerSet][0] ?? '') : MULTI_PAYER;

  const wantGroup = params.get('group');
  // auto-open the first late claim on the bare waiting tab (desktop)
  useEffect(() => {
    if (view === 'waiting' && !claimId && isDesktop) {
      const first = wantGroup === 'pays-patient' ? (paysRows[0] ?? lateRows[0]) : lateRows[0];
      if (first) void navigate(`/ar/${first.id}${keepSearch}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, claimId, isDesktop, lateRows[0]?.id, paysRows[0]?.id]);

  // /ar?group=late — open with the Late group in view (it is always expanded).
  // /ar?group=pays-patient — same, with "The payer pays the patient" expanded.
  const scrolledFor = useRef('');
  useEffect(() => {
    if (view !== 'waiting' || (wantGroup !== 'late' && wantGroup !== 'pays-patient' && wantGroup !== 'not-followed') || scrolledFor.current === wantGroup) return;
    const el = document.getElementById(wantGroup === 'late' ? 'iar-late-grp' : wantGroup === 'not-followed' ? 'iar-nf-grp' : 'iar-pays-grp');
    if (!el) return;
    scrolledFor.current = wantGroup;
    el.scrollIntoView({ block: 'start' });
  });

  const addToast = (msg: string, undo?: { label: string; onUndo: () => void }) => {
    const id = ++toastSeq.current;
    setToasts((t) => [...t, { id, msg, ...(undo ? { undoLabel: undo.label, onUndo: undo.onUndo } : {}) }]);
    setTimeout(() => { setToasts((t) => t.filter((x) => x.id !== id)); }, 8000);
  };

  const header = (
    <Header
      tab={view}
      owed={getOwedSync()}
      claimCount={getClaimCountSync()}
      lateCount={lateCountSync()}
      buckets={getBucketsSync()}
      waitingCount={waitingCountSync()}
      writtenOffCount={writtenOffCountSync()}
      writtenOffTotal={writtenOffTotalSync()}
      activeBucket={bucket}
      onBucket={(label) => { patchParams({ age: bucket === label ? null : (AGE_TO_PARAM[label] ?? null) }); }}
      tabTo={(t) => (t === 'written-off' ? '/ar/written-off' : '/ar')}
      settingsTo="/settings/follow-up"
    />
  );

  // ---- Written off tab ----
  if (view === 'written-off') {
    return (
      <div className="iar iar-page">
        {header}
        <WrittenOff
          rows={getWrittenOffSync()}
          total={writtenOffTotalSync()}
          count={writtenOffCountSync()}
          onAddReason={(id, reason, note) => {
            addWriteOffReason(id, reason, note);
            addToast('Reason added.');
          }}
        />
        {renderToasts()}
      </div>
    );
  }

  // ---- Empty ----
  if (late.length === 0 && ontime.length === 0 && notFollowed.length === 0) {
    return (
      <div className="iar iar-page">
        {header}
        <div className="iar-empty">
          <p className="iar-empty-title">Insurance owes you nothing right now.</p>
          <p className="iar-meta">Every claim sent has been paid.</p>
        </div>
        {renderToasts()}
      </div>
    );
  }

  const mobileClass = !isDesktop ? (claimId ? 'iar-two-detail' : 'iar-two-list') : '';

  let detail: React.ReactNode = null;
  if (claimId) {
    if (!selected) {
      detail = <NotFoundInPage as="main" thing="claim" id={claimId} listLabel="claims waiting for payment" listTo="/ar" />;
    } else {
      detail = (
        <Detail
          key={selected.id}
          claim={selected}
          onStop={() => { setStopOpen(true); }}
          onOpenHitl={(itemId) => { void navigate(`/human-in-the-loop/${itemId}`); }}
          onChangeLimit={() => { void navigate('/settings/follow-up'); }}
          onResume={() => {
            resumeFollowUp(selected.id);
            addToast(`Resumed following up on ${selected.patient}’s claim.`);
          }}
          {...(!isDesktop ? { onBack: () => { void navigate(`/ar${keepSearch}`); } } : {})}
        />
      );
    }
  }

  const goNext = (curId: string) => {
    const first =
      lateRows.find((c) => c.id !== curId) ??
      ontimeRows.find((c) => c.id !== curId) ??
      notFollowedRows.find((c) => c.id !== curId) ??
      paysRows.find((c) => c.id !== curId);
    if (first) void navigate(`/ar/${first.id}${keepSearch}`);
    else void navigate(`/ar${keepSearch}`);
  };

  return (
    <div className={`iar iar-page ${mobileClass}`}>
      {header}
      <div className="iar-body">
        <List
          lateRows={lateRows}
          ontimeRows={ontimeRows}
          {...(claimId ? { selectedId: claimId } : {})}
          search={search}
          payer={payerValue}
          payerOptions={payerOptions}
          focusPayers={[...payerSet]}
          multiPayerCount={payerSet.size}
          notFollowedRows={notFollowedRows}
          paysRows={paysRows}
          openPays={wantGroup === 'pays-patient'}
          onSearch={(v) => { patchParams({ q: v }); }}
          onPayerSelect={(v) => {
            if (v === MULTI_PAYER) return;
            const slug = AR_SLUG_BY_NAME(v);
            patchParams({ payer: v === '' ? null : slug });
          }}
          onSelect={(id) => { void navigate(`/ar/${id}${keepSearch}`); }}
        />
        {detail}
      </div>
      {stopOpen && selected ? (
        <StopDialog
          claim={selected}
          onCancel={() => { setStopOpen(false); }}
          onConfirm={(v) => {
            const name = selected.patient;
            const payerShort = selected.payer.short;
            const id = selected.id;
            const outcome = stopFollowUp(id, v);
            setStopOpen(false);
            if (v.reason === 'write_off') {
              addToast(`Wrote off ${name}’s ${payerShort} claim. It’s in Written off.`);
            } else if (v.reason === 'patient_pays') {
              addToast(
                `Moved to the patient. ${getMovedToPatientText(id) ?? ''}`.trim(),
                { label: 'Open record', onUndo: () => { void navigate(`/claims/record/${id}`); } },
              );
            } else if (v.reason === 'pause') {
              addToast(`Paused ${name}’s claim. It’s in ELVA isn’t following up.`);
            } else if (outcome === 'stays') {
              addToast(`Linked ${name}’s claim to the other insurance. ELVA keeps following up.`);
            } else if (outcome === 'not-followed') {
              addToast(`ELVA isn’t following up on ${name}’s claim. It still counts as owed.`);
            } else {
              addToast(`Stopped following up on ${name}’s claim.`);
            }
            if (outcome !== 'stays') goNext(selected.id);
          }}
        />
      ) : null}
      {renderToasts()}
    </div>
  );

  function renderToasts() {
    if (toasts.length === 0) return null;
    return (
      <div className="iar-toasts">
        {toasts.map((t) => (
          <div key={t.id} className="iar-toast" role="status">
            <span>{t.msg}</span>
            {t.undoLabel ? (
              <button type="button" className="iar-toast-undo" onClick={() => { t.onUndo?.(); setToasts((list) => list.filter((x) => x.id !== t.id)); }}>{t.undoLabel}</button>
            ) : null}
          </div>
        ))}
      </div>
    );
  }
}
